import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError } from '../api/client'
import { LangueCourante } from './langue'
import { LANGUAGES, MESSAGES, RTL_LANGUAGES } from './messages'
import type { Language, MessageKey } from './messages'

const STORAGE_KEY = 'agri.language'

interface I18nValue {
  language: Language
  direction: 'ltr' | 'rtl'
  setLanguage: (language: Language) => void
  t: (key: MessageKey) => string
  /**
   * Traduit un code d'erreur renvoyé par l'API. Un code sans traduction rend le message du
   * serveur (`detail`) s'il est fourni, sinon le message générique : jamais une chaîne vide.
   */
  translateError: (code: string | undefined, detail?: string) => string
  /** Message à afficher pour une erreur attrapée : code traduit, sinon détail serveur, sinon réseau / générique. */
  describeError: (cause: unknown) => string
}

const I18nContext = createContext<I18nValue | null>(null)

function isLanguage(value: string | null): value is Language {
  return value !== null && (LANGUAGES as readonly string[]).includes(value)
}

/** Langue initiale : le choix déjà fait par l'utilisateur, sinon celle du navigateur, sinon le français. */
function detectLanguage(): Language {
  const stored = window.localStorage.getItem(STORAGE_KEY)

  if (isLanguage(stored)) {
    return stored
  }

  for (const candidate of navigator.languages ?? []) {
    const primary = candidate.split('-')[0]
    if (isLanguage(primary ?? null)) {
      return primary as Language
    }
  }

  return 'fr'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage)

  const direction = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr'

  // La direction est portée par <html> : c'est elle qui fait basculer toute la mise en page,
  // marges et alignements compris, sans feuille de style dédiée à l'arabe.
  // useLayoutEffect pour appliquer avant le premier paint (évite un flash LTR en arabe).
  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = direction
    document.documentElement.setAttribute('data-lang', language)
  }, [language, direction])

  const setLanguage = useCallback((next: Language) => {
    window.localStorage.setItem(STORAGE_KEY, next)
    setLanguageState(next)
  }, [])

  const value = useMemo<I18nValue>(() => {
    const table = MESSAGES[language]

    // Une clé absente rend la clé elle-même plutôt que `undefined` : un toast vide ne dit
    // rien, un code affiché se cherche dans le dictionnaire.
    const t = (key: MessageKey) => table[key] ?? String(key)

    const translateError = (code: string | undefined, detail?: string) => {
      if (code && code in table) {
        return table[code as MessageKey]
      }

      if (detail && detail.trim() !== '') {
        return detail
      }

      return table['error.unexpected']
    }

    return {
      language,
      direction,
      setLanguage,
      t,
      translateError,
      describeError: (cause) => {
        if (cause instanceof ApiError) {
          return cause.code === 'error.network'
            ? table['error.network']
            : translateError(cause.code, cause.message)
        }

        return table['error.unexpected']
      },
    }
  }, [language, direction, setLanguage])

  return (
    <I18nContext.Provider value={value}>
      <LangueCourante.Provider value={language}>{children}</LangueCourante.Provider>
    </I18nContext.Provider>
  )
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext)

  if (context === null) {
    throw new Error('useI18n doit être appelé à l’intérieur de <I18nProvider>.')
  }

  return context
}
